// base
import { useEffect, useMemo, useRef, useState } from 'react';

// modules
import {
  CameraControllerTypeAtom,
  CoordinateAtom,
  PlayerAlramTextAtom,
  PlayerDataAtom,
  SiteInformationDataAtom
} from 'modules';

// variables
import { PLAYER_MOVE_SPEED } from 'variables';

// libraries
import { vec3 } from '@react-three/rapier';
import { Quaternion, Vector3 } from 'three';
import { SkeletonUtils } from 'three-stdlib';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useFrame, useGraph, useThree } from '@react-three/fiber';
import { useAnimations, useGLTF, useKeyboardControls } from '@react-three/drei';

let directionVector = new Vector3();
let cameraDirection = new Vector3();
let rotateAngle = new Vector3(0, 1, 0);
let rotateQuarternion = new Quaternion();

export const usePlayerControls = (props) => {
  const { controlsRef, position, usePlayerControl } = props;

  const textRef = useRef();
  const cameraRef = useRef();
  const playerRef = useRef();
  const controlRef = useRef();
  const rigidbodyRef = useRef();

  const [prevPosition, setPrevPosition] = useState();
  const [animation, setAnimation] = useState('idle');

  const player = useRecoilValue(PlayerDataAtom);
  const cameraType = useRecoilValue(CameraControllerTypeAtom);
  const { floor: selectedFloor } = useRecoilValue(SiteInformationDataAtom);

  const setCoordinate = useSetRecoilState(CoordinateAtom);
  const setPlayerAlarmText = useSetRecoilState(PlayerAlramTextAtom);

  const playerId = player?.id;

  const [_, get] = useKeyboardControls();
  const { camera: sceneCamera, gl, raycaster, scene: threeScene } = useThree();
  const { scene, animations } = useGLTF(`/assets/models/characters/character-vsc.glb`);

  const memoizedPosition = useMemo(() => position, []);

  const clone = useMemo(() => SkeletonUtils.clone(scene), [scene]);

  const objectMap = useGraph(clone);

  const nodes = objectMap.nodes;
  const materials = objectMap.materials;

  const { actions } = useAnimations(animations, playerRef);

  // 카메라 타겟 설정
  const updateCameraTarget = (moveX, moveZ) => {
    sceneCamera.position.x += moveX;
    sceneCamera.position.z += moveZ;

    const playerWorldPos = vec3(rigidbodyRef.current.translation());

    cameraDirection.x = playerWorldPos.x;
    cameraDirection.y = playerWorldPos.y + 1;
    cameraDirection.z = playerWorldPos.z;

    if (cameraType === 'threePerson') {
      controlRef.current.target.copy(cameraDirection);
    }

    if (cameraType === 'onePerson') {
      sceneCamera.position.x = playerWorldPos.x;
      sceneCamera.position.y = cameraDirection.y + 140;
      sceneCamera.position.z = playerWorldPos.z;
    }

    sceneCamera.near = 0.01;
    sceneCamera.far = 100000;
    sceneCamera.fov = 1000000;
  };

  // 땅 위에 있는지 체크
  const isGrounded = (frameScene) => {
    if (!frameScene || !frameScene.children) return false;

    const direction = new Vector3(0, -1, 0).normalize();
    const playerWorldPos = vec3(rigidbodyRef.current.translation());

    raycaster.far = 1000;
    raycaster.set(playerWorldPos, direction);

    const intersects = raycaster
      .intersectObjects(frameScene.children, true)
      .filter((item) => item.object && item.object.isMesh);

    // frameScene.add(new ArrowHelper(raycaster.ray.direction, raycaster.ray.origin, 300, 0x00ff00));

    if (intersects.length > 0) {
      if (intersects[0].object.visible) return true;

      return false;
    } else {
      return false;
    }
  };

  // 애니메이션 작동
  useEffect(() => {
    if (actions[animation]) {
      actions[animation].reset().fadeIn(0.2).play();
    }

    return () => {
      if (actions[animation]) {
        actions[animation].fadeOut(0.2);
      }
    };
  }, [actions, animations]);

  // 카메라 타켓 초기화
  useEffect(() => {
    if (!playerRef.current || !usePlayerControl) return;

    updateCameraTarget(0, 0);
  }, [cameraType, playerRef.current, selectedFloor, usePlayerControl]);

  // 프레임마다 실행 - requestAnimationFrame 역할
  useFrame(({ scene: frameScene }, delta) => {
    if (!player) return;
    if (!playerRef.current) return;
    if (!rigidbodyRef.current) return;
    if (!usePlayerControl) {
      rigidbodyRef.current.setTranslation(position);

      return;
    }

    let directionOffset = 0;

    updateCameraTarget(0, 0);

    const { up, down, left, right, close } = get();

    const isKeyboard = up || down || left || right;

    const playerWorldPos = vec3(rigidbodyRef.current.translation());

    const angleYCameraDirection = Math.atan2(
      sceneCamera.position.x - playerWorldPos.x,
      sceneCamera.position.z - playerWorldPos.z
    );

    // 키보드 이벤트 작동
    if (usePlayerControl) {
      if (isKeyboard) {
        setAnimation('run');

        // 키보드 이벤트에 따른 방향 설정
        if (up) {
          if (left) {
            directionOffset = Math.PI / 4;
          } else if (right) {
            directionOffset = -Math.PI / 4;
          }
        } else if (down) {
          if (left) {
            directionOffset = Math.PI / 4 + Math.PI / 2;
          } else if (right) {
            directionOffset = -Math.PI / 4 - Math.PI / 2;
          } else {
            directionOffset = Math.PI;
          }
        } else if (left) {
          directionOffset = Math.PI / 2;
        } else if (right) {
          directionOffset = -Math.PI / 2;
        }

        // 현재 땅위 있는지 체크
        if (!isGrounded(frameScene)) {
          setPlayerAlarmText('🚨 추락주의!');

          rigidbodyRef.current.setTranslation(prevPosition);

          setTimeout(() => {
            setPlayerAlarmText('🚨 안전조치 필수!');
          }, 1000);
        } else {
          rotateQuarternion.setFromAxisAngle(rotateAngle, angleYCameraDirection + directionOffset);
          rigidbodyRef.current.setRotation(
            new Vector3(0, angleYCameraDirection + directionOffset, 0)
          );
          playerRef.current.quaternion.rotateTowards(rotateQuarternion, 0.5);

          setPrevPosition(rigidbodyRef.current.translation());

          sceneCamera.getWorldDirection(directionVector);

          directionVector.y = 0;
          directionVector.normalize();
          directionVector.applyAxisAngle(rotateAngle, directionOffset);

          const moveX = directionVector.x * PLAYER_MOVE_SPEED * delta;
          const moveZ = directionVector.z * PLAYER_MOVE_SPEED * delta;

          const newPosition = new Vector3(
            playerWorldPos.x + moveX,
            playerWorldPos.y,
            playerWorldPos.z + moveZ
          );

          rigidbodyRef.current.setTranslation(newPosition);

          updateCameraTarget(moveX, moveZ);
        }
      } else {
        setAnimation('idle');
      }
    }

    const newPlayerWorldPos = vec3(rigidbodyRef.current.translation());

    if (textRef.current) {
      textRef.current.position.set(
        newPlayerWorldPos.x,
        newPlayerWorldPos.y + 200,
        newPlayerWorldPos.z
      );

      textRef.current.lookAt(10000, 10000, 10000);
    }

    setCoordinate({
      x: newPlayerWorldPos.x.toFixed(4),
      y: newPlayerWorldPos.y.toFixed(4),
      z: newPlayerWorldPos.z.toFixed(4)
    });
  });

  return {
    rigidbodyRef,
    controlsRef,
    controlRef,
    playerId,
    playerRef,
    textRef,
    cameraRef,
    memoizedPosition,
    nodes,
    materials
  };
};
